import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Search, UserCheck, UserPlus, Calendar, Trash2, Shield, DollarSign } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminUsersTab() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const fetchUsers = useCallback(() => {
    api.get('/admin/users').then(r => setUsers(r.data)).catch(e => process.env.NODE_ENV === 'development' && console.error(e));
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      setDeleting(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filtered = useMemo(() => users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filter === 'all' || u.role === filter;
    return matchSearch && matchRole;
  }), [users, search, filter]);

  const instructors = useMemo(() => users.filter(u => u.role === 'instructor'), [users]);
  const parents = useMemo(() => users.filter(u => u.role === 'parent'), [users]);

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1E1B4B] flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-[#C4F017]" />
            </div>
            <div><p className="text-2xl font-extrabold text-slate-800">{users.length}</p><p className="text-xs text-slate-500 whitespace-nowrap">Total Users</p></div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0E7490] flex items-center justify-center shrink-0">
              <UserCheck className="w-5 h-5 text-white" />
            </div>
            <div><p className="text-2xl font-extrabold text-slate-800">{instructors.length}</p><p className="text-xs text-slate-500 whitespace-nowrap">Instructors</p></div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#22D3EE] flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div><p className="text-2xl font-extrabold text-slate-800">{parents.length}</p><p className="text-xs text-slate-500 whitespace-nowrap">Parents</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-10 rounded-xl" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} data-testid="users-search" />
        </div>
        <div className="flex gap-2">
          {['all', 'instructor', 'parent'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all capitalize ${
                filter === f ? 'bg-[#1E1B4B] text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f + 's'}
            </button>
          ))}
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-2">
        {filtered.map(u => (
          <Card key={u.user_id} className="rounded-2xl border-slate-100" data-testid={`user-row-${u.user_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                  u.role === 'instructor' ? 'bg-[#1E1B4B]' : 'bg-[#0E7490]'
                }`}>
                  {(u.name || u.email || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate text-sm">{u.name || 'No Name'}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                {!u.is_admin && (
                  <Button size="sm" variant="ghost" className="text-slate-300 hover:text-red-500 h-8 w-8 p-0 shrink-0" onClick={() => setDeleting(u)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {(u.roles || [u.role]).map(r => (
                  <Badge key={r} className={`rounded-full capitalize text-[11px] ${
                    r === 'instructor' ? 'bg-[#1E1B4B] text-white' : r === 'facility_manager' ? 'bg-[#0E7490] text-white' : 'bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]'
                  }`}>
                    {r === 'facility_manager' ? 'Facility' : r}
                  </Badge>
                ))}
                {u.is_admin && <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-300 text-[11px]">Admin</Badge>}
                {u.has_payroll && <Badge className="rounded-full bg-green-100 text-green-700 border-green-300 text-[11px]">Payroll</Badge>}
                {/* Admin toggle — can't demote yourself */}
                {u.user_id !== currentUser?.user_id && (
                  <Button size="sm" variant="ghost" className={`h-7 px-2 text-[10px] font-bold ${u.is_admin ? 'text-amber-600 hover:text-amber-800' : 'text-slate-400 hover:text-amber-600'}`} onClick={async () => {
                    try { 
                      const res = await api.put(`/admin/users/${u.user_id}/toggle-admin`);
                      toast.success(`${u.name || u.email} is ${res.data.is_admin ? 'now an admin' : 'no longer an admin'}`);
                      fetchUsers();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                  }} data-testid={`toggle-admin-${u.user_id}`}>
                    <Shield className="w-3 h-3 mr-1" /> {u.is_admin ? 'Remove Admin' : '+Admin'}
                  </Button>
                )}
                {/* Payroll toggle — only visible to admins with payroll, only for other admins */}
                {u.is_admin && u.user_id !== currentUser?.user_id && currentUser?.has_payroll && (
                  <Button size="sm" variant="ghost" className={`h-7 px-2 text-[10px] font-bold ${u.has_payroll ? 'text-green-600 hover:text-green-800' : 'text-slate-400 hover:text-green-600'}`} onClick={async () => {
                    try {
                      const res = await api.put(`/admin/users/${u.user_id}/toggle-payroll`);
                      toast.success(`${u.name || u.email} ${res.data.has_payroll ? 'now has payroll access' : 'no longer has payroll access'}`);
                      fetchUsers();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                  }} data-testid={`toggle-payroll-${u.user_id}`}>
                    <DollarSign className="w-3 h-3 mr-1" /> {u.has_payroll ? 'Remove Payroll' : '+Payroll'}
                  </Button>
                )}
                {!(u.roles || [u.role]).includes('instructor') && (
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-[#1E1B4B] h-7 px-2 text-[10px] font-bold" onClick={async () => {
                    try { await api.put(`/admin/users/${u.user_id}/add-role`, { role: 'instructor' }); toast.success(`${u.name || u.email} is now also an instructor`); fetchUsers(); } catch (e) { toast.error('Failed'); }
                  }} data-testid={`add-instructor-role-${u.user_id}`}>
                    <UserPlus className="w-3 h-3 mr-1" /> +Instructor
                  </Button>
                )}
                {!(u.roles || [u.role]).includes('parent') && u.role !== 'facility_manager' && (
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-[#0E7490] h-7 px-2 text-[10px] font-bold" onClick={async () => {
                    try { await api.put(`/admin/users/${u.user_id}/add-role`, { role: 'parent' }); toast.success(`${u.name || u.email} is now also a parent`); fetchUsers(); } catch (e) { toast.error('Failed'); }
                  }} data-testid={`add-parent-role-${u.user_id}`}>
                    <UserPlus className="w-3 h-3 mr-1" /> +Parent
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-slate-400 py-8">No users found</p>
        )}
      </div>

      {/* Delete User Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><Trash2 className="w-5 h-5" /> Delete User</DialogTitle>
            <DialogDescription className="sr-only">Confirm user deletion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Permanently delete <strong>{deleting?.name || deleting?.email || 'this user'}</strong>? This will unlink all their children, schedules, and data.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={() => deleteUser(deleting?.user_id)} data-testid="confirm-delete-user"><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
